import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getCompetitors } from 'apis/CommunityAPI';
import { getAIFeedbacks } from 'apis/FeedbackAPI';
import { getAICompetitiveIntelligence, getCommunityActivitySummary } from 'apis/RecommendationsAPI';
import RecommendationsNotFound from 'components/AiCompetitiveIntelligence/RecommendationsNotFound';
import WeeklyDigestCard from 'components/CmiDigestOverview/WeeklyDigestCard';
import { AI_FEEDBACK_FEATURE_TYPES } from 'components/Feedback/constants';
import Feedback from 'components/Feedback/Feedback';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { VIEW_CATEGORY } from 'components/View/constants';
import { hasCmiDigestFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import ActivitySummaryCard from './ActivitySummaryCard';
import CategoryBreakdownCard from './CategoryBreakdownCard';
import CommunityAIOverviewHeader from './CommunityAIOverviewHeader';
import MarketPositionCard from './MarketPositionCard';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div`
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: var(--background-gray-light);
  border-top: 1px solid var(--line-gray);
`;

const CardWrapper = styled.div`
  max-width: 1175px;
  width: 100%;
  margin: auto;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 60px;
`;

const CommunityAIOverview = () => {
  const { selectedCommunity, views } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);
  const showWeeklyDigest = hasCmiDigestFlag(currentUser);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence', selectedCommunity?.id],
    queryFn: () => getAICompetitiveIntelligence(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const { data: competitors } = useQuery({
    queryKey: ['competitors', selectedCommunity?.id],
    queryFn: () => getCompetitors(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const { data: activitySummary } = useQuery({
    queryKey: ['community-activity-summary', selectedCommunity?.id],
    queryFn: () => getCommunityActivitySummary(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const { data: feedback } = useQuery({
    queryKey: ['ai-feedbacks', 'community-ai-overview', selectedCommunity?.id],
    queryFn: () =>
      getAIFeedbacks({
        community_id: selectedCommunity?.id,
        feature_type: AI_FEEDBACK_FEATURE_TYPES.COMMUNITY_AI_OVERVIEW,
      }),
    select: (feedbacks) => feedbacks?.[0] ?? null,
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const competingCommunitiesCount = useMemo(
    () => (Array.isArray(competitors) ? competitors.length + 1 : undefined),
    [competitors]
  );

  const pricingGridViewId = useMemo(
    () => (views ?? []).find((view: any) => view.category === VIEW_CATEGORY.OVERVIEW)?.id,
    [views]
  );

  const isEmpty = useMemo(() => !data || data?.ranking == null, [data]);

  return (
    <Container data-test-id="community-ai-overview-page">
      <CommunityAIOverviewHeader
        rightSlot={
          selectedCommunity?.id ? (
            <Feedback
              subtitle={selectedCommunity?.name || ''}
              featureType={AI_FEEDBACK_FEATURE_TYPES.COMMUNITY_AI_OVERVIEW}
              community={selectedCommunity.id}
              sourceUrl={window.location.href}
              existingFeedback={feedback}
            />
          ) : undefined
        }
      />
      <Main>
        {isLoading && (
          <LoaderContainer data-test-id="community-ai-overview-loader">
            <LoaderRibbed active />
          </LoaderContainer>
        )}
        {!isLoading && isEmpty && <RecommendationsNotFound />}
        {!isLoading && !isEmpty && (
          <>
            <CardWrapper>
              <MarketPositionCard
                data={data}
                communityId={selectedCommunity?.id}
                competingCommunitiesCount={competingCommunitiesCount}
                pricingGridViewId={pricingGridViewId}
              />
            </CardWrapper>
            <CardWrapper>
              <CategoryBreakdownCard
                data={data}
                communityId={selectedCommunity?.id}
                competingCommunitiesCount={competingCommunitiesCount}
              />
            </CardWrapper>
          </>
        )}
        {!isLoading && showWeeklyDigest && (
          <CardWrapper>
            <WeeklyDigestCard communityId={selectedCommunity?.id} />
          </CardWrapper>
        )}
        {!isLoading && !isEmpty && (
          <CardWrapper>
            <ActivitySummaryCard data={activitySummary} communityId={selectedCommunity?.id} />
          </CardWrapper>
        )}
      </Main>
    </Container>
  );
};

export default CommunityAIOverview;
